import { TranscriptEntry, User } from '@highlight/common'
import { TranscriptMessage } from '@renderer/windows/features/side-panel/types'
import {
  isSingleMicSpeaker,
  resolveHostSpeaker,
  type HostSpeakerContext,
  type UserVoiceMatches,
} from '@shared/utils/audio-notes/mic-label'
import { GlobalState } from '@highlight/ipc'
import { USER_STATE } from '@shared/ipc/GlobalStateUtils'
import { getUserFullName } from '@renderer/utils/user-name'

/**
 * Removes the "Captured with Highlight" footer from audio note summaries
 */
export function cleanAudioNoteSummary(summary: string | undefined): string {
  if (!summary) return ''
  return summary.replace(/\n\nCaptured with \[Highlight\]\(https:\/\/highlightai\.com\)\.$/g, '')
}

/**
 * Snapshot the current owner name from GlobalState. Callers that need reactive
 * updates (e.g. Jotai-backed live transcript atoms) should resolve the name
 * themselves via atomWithGlobalState(USER_STATE) and pass it in; everyone else
 * gets the current value at render time.
 */
const currentOwnerNameSnapshot = (): string | undefined =>
  getUserFullName(GlobalState.get<User | undefined>(USER_STATE))

export interface SpeakerResolutionContext extends HostSpeakerContext {
  /** dgSpeakerIndex → name: auto-resolved system speakers merged with manual overrides. */
  resolvedNames?: Record<string, string>
}

/**
 * The name a transcript entry displays under. The rename UI matches against
 * this too, so the two can't drift. A rename wins, then `resolveHostSpeaker`
 * for the mic.
 */
export const resolveEntrySpeaker = (entry: TranscriptEntry, ctx: SpeakerResolutionContext): string => {
  const { resolvedNames } = ctx
  if (resolvedNames && entry.dgSpeakerIndex && resolvedNames[entry.dgSpeakerIndex]) {
    return resolvedNames[entry.dgSpeakerIndex]
  }
  return entry.isHost ? resolveHostSpeaker(entry, ctx) : entry.speaker
}

/** The clock time shown on a transcript turn's header, e.g. "2:56 PM". */
export function formatTranscriptTime(date: Date): string {
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
}

export const mapTranscriptEntriesToMessages = (
  transcriptEntries: Array<TranscriptEntry>,
  resolvedNames?: Record<string, string>,
  ownerNameOverride?: string,
  userVoices?: UserVoiceMatches,
): Array<TranscriptMessage> => {
  if (!transcriptEntries || transcriptEntries.length === 0) {
    return []
  }

  const ownerName = ownerNameOverride ?? currentOwnerNameSnapshot()

  // Computed over the whole list so the label doesn't vary per entry.
  const singleMicSpeaker = isSingleMicSpeaker(transcriptEntries)

  const resolveSpeaker = (entry: TranscriptEntry): string =>
    resolveEntrySpeaker(entry, { resolvedNames, ownerName, singleMicSpeaker, userVoices })

  // Sort entries by timestamp to ensure correct message combining.
  //
  // This runs on every transcript delta over the whole meeting, so both the
  // allocation and the sort itself are worth avoiding: `Date.parse` returns the
  // epoch millis directly instead of allocating two `Date` objects per
  // comparison, and the live feed appends in order — so check once in O(n) and
  // skip both the sort and the array copy when the input is already sorted.
  // Saved/merged buffers that aren't ordered still fall through to the sort.
  let isSorted = true
  for (let i = 1; i < transcriptEntries.length; i++) {
    if (Date.parse(transcriptEntries[i - 1].timestamp.absolute) > Date.parse(transcriptEntries[i].timestamp.absolute)) {
      isSorted = false
      break
    }
  }
  const sortedEntries = isSorted
    ? transcriptEntries
    : [...transcriptEntries].sort((a, b) => Date.parse(a.timestamp.absolute) - Date.parse(b.timestamp.absolute))

  const messages: Array<TranscriptMessage> = []
  let currentMessage: TranscriptMessage | null = null
  let currentIsHost: boolean | undefined
  let currentDgIndices: Set<string> | null = null

  for (const entry of sortedEntries) {
    const resolvedSpeaker = resolveSpeaker(entry)
    const shouldCombine =
      currentMessage &&
      currentMessage.sender === resolvedSpeaker &&
      currentIsHost === entry.isHost &&
      Math.abs(Date.parse(entry.timestamp.absolute) - Date.parse(currentMessage.time)) <= 10000

    if (shouldCombine && currentMessage) {
      currentMessage.text += ' ' + entry.text
      currentMessage.segments.push({ time: entry.timestamp.absolute, text: entry.text })
      if (entry.dgSpeakerIndex && currentDgIndices) {
        currentDgIndices.add(entry.dgSpeakerIndex)
        currentMessage.dgSpeakerIndices = Array.from(currentDgIndices)
      }
    } else {
      if (currentMessage) {
        messages.push(currentMessage)
      }
      currentIsHost = entry.isHost
      currentDgIndices = entry.dgSpeakerIndex ? new Set([entry.dgSpeakerIndex]) : null
      currentMessage = {
        sender: resolvedSpeaker,
        text: entry.text,
        segments: [{ time: entry.timestamp.absolute, text: entry.text }],
        time: entry.timestamp.absolute,
        displayTime: formatTranscriptTime(new Date(entry.timestamp.absolute)),
        isHost: entry.isHost,
        dgSpeakerIndices: entry.dgSpeakerIndex ? [entry.dgSpeakerIndex] : undefined,
      }
    }
  }

  // Don't forget to push the last message
  if (currentMessage) {
    messages.push(currentMessage)
  }

  return messages
}

/**
 * Folds each run of messages from one speaker into a single turn, for display:
 * the transcript views grow the turn being read, so live speech streams into
 * one row instead of a new one starting whenever a speaker pauses past the 10s
 * combine window. Copy and export keep `mapTranscriptEntriesToMessages`' 10s
 * paragraphs, which is why this is a second pass rather than a wider window
 * there. The merged turn keeps the run's first `time` (its row key) and every
 * segment (the reading anchors).
 */
export function mergeSpeakerTurns(messages: Array<TranscriptMessage>): Array<TranscriptMessage> {
  const turns: Array<TranscriptMessage> = []
  for (const message of messages) {
    const previous = turns[turns.length - 1]
    if (!previous || previous.sender !== message.sender || previous.isHost !== message.isHost) {
      turns.push(message)
      continue
    }
    const dgSpeakerIndices = new Set([...(previous.dgSpeakerIndices ?? []), ...(message.dgSpeakerIndices ?? [])])
    turns[turns.length - 1] = {
      ...previous,
      text: `${previous.text} ${message.text}`,
      segments: [...previous.segments, ...message.segments],
      dgSpeakerIndices: dgSpeakerIndices.size > 0 ? Array.from(dgSpeakerIndices) : undefined,
    }
  }
  return turns
}

/**
 * Formats transcript messages into a copyable text format
 */
export function formatTranscriptForCopy(messages: TranscriptMessage[]): string {
  if (!messages || messages.length === 0) return ''

  // Sort messages by time
  const sortedMessages = messages.slice().sort((a, b) => {
    const dateA = new Date(a.time)
    const dateB = new Date(b.time)
    return dateA.getTime() - dateB.getTime()
  })

  return sortedMessages.map((msg) => `${msg.sender} - ${msg.displayTime}: ${msg.text}`).join('\n\n')
}
